import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'

import StepIndicator from '../../components/step-indicator'
import GiftAid from '../../components/gift-aid'
import PaymentDetails from '../../components/payment-details'
import SubmitButtons from '../../components/submit-buttons'

import MembershipFormContext from '../../context/membership'
import initialValues from '../../data/initialValues'
import validation from '../../validation/membership'
import { getSS, setSS, cleanObject, loadIndexPage } from '../../helpers'
import { saveMembership } from '../../api'

const Payment: React.FC = (props: any) => {

  const navigate = useNavigate()
  const params = useParams()
  const membershipForm = useContext(MembershipFormContext)
  const membership_form = (window as any).KEW.collections.membership_form[0]
  const [ dataLayerInitialPush3, setDataLayerInitialPush3 ] = useState(getSS('dataLayerInitialPush3'))
  const [ payment, setPayment ] = useState(initialValues.payment)
  const [ submittingPayPal, setSubmittingPayPal ] = useState(false)
  const url = membership_form.private_form ? 'private/' + params.url : params.url
  let membership = getSS('membership')
  const wakehurstMembership = getSS('membership')?.selected_membership?.wakehurst_membership

  if (!membership || membership.process_payment) {
    loadIndexPage(navigate, url)
  }

  useEffect(() => {
    document.title = 'Payment - ' + (window as any).KEW.collections.membership_form[0].meta_title
  }, [])

  useEffect(() => {
    membershipForm.preSelectedMembership ? membershipForm.setCurrentStep(2) : membershipForm.setCurrentStep(3)
    if (getSS('payment')) {
      setPayment(getSS('payment'))
    }
  }, [membershipForm])

  const braintreePayPalInit = (membership: any) => {

    membershipForm.showLoading()

    const paypalButton: any = document.querySelector('#m-pay-by-paypal-button');
    // PayPal
    // Create a client.
    (window as any).braintree.client.create({
        authorization: membership.payment_token
    }, function(clientErr: any, clientInstance: any) {

      // Stop if there was a problem creating the client.
      // This could happen if there is a network error or if the authorization
      // is invalid.
      if (clientErr) {
          console.error('Error creating client:', clientErr);
          return;
      }

      // Create a PayPal component.
      (window as any).braintree.paypal.create({
          client: clientInstance
      }, function(paypalErr: any, paypalInstance: any) {

        // Stop if there was a problem creating PayPal.
        // This could happen if there was a network error or if it's incorrectly
        // configured.
        if (paypalErr) {
            console.error('Error creating PayPal:', paypalErr);
            return;
        }

        // Because tokenization opens a popup, this has to be called as a result of
        // customer action, like clicking a button—you cannot call this at any time.
        paypalButton.addEventListener('click', function() {

          paypalInstance.tokenize({
              flow: 'checkout',
              amount: membership.amount,
              currency: 'GBP'
          }, async function(tokenizeErr: any, payload: any) {

            // Stop if there was an error.
            if (tokenizeErr) {
                if (tokenizeErr.type !== 'CUSTOMER') {
                    console.error('Error tokenizing:', tokenizeErr);
                    if (tokenizeErr.code === 'PAYPAL_POPUP_OPEN_FAILED') {
                      alert('It looks like pop-up windows are disabled for this website. These need to be allowed for payment via PayPal - please enable them or try a different payment method.')
                    }
                }
                // hide loading
                membershipForm.hideLoading()
                return;
            }

            // NOTE: payment method nonces expire after 3 hours
            membership.payment_method_token_or_nonce = payload.nonce;

            // Update the membership record attempting to process the payment
            membership.process_payment = true;
            if (membership.optins_giftaid) {
              membership.giftaid_amount = (membership.amount*0.25).toFixed(2);
            }

            const savedMembership: any = await saveMembership(membership)
            console.log(savedMembership.data);
            if (savedMembership?.status === 200) {
              setSS('membership', savedMembership.data);
              membershipForm.hideLoading();
              navigate(`/membership/${url}/thanks`)
            } else {
              setSS('api-error', savedMembership.headers['api-error'])
              membershipForm.hideLoading()
              navigate(`/membership/${url}/failed`)
            }
          });

        });
        if (!submittingPayPal) {
          setSubmittingPayPal(true)
          paypalButton.click()
          // return
        }
      });
    });

  }

  function onSubmit(data: any) {
    console.log(data);
    setSS('payment', data)
    membership = { ...membership, ...data }
    setSS('membership', membership);

    if (!dataLayerInitialPush3) {
      const formType = membership_form.form_type === 'standard' ? 'new' :
        membership_form.form_type === 'upgrade' ? 'upgrade' :
        membership_form.form_type === 'renewal' ? 'renewal' :
        membership_form.form_type === 'ingarden' ? 'new' :
        membership_form.form_type === 'daypass' ? 'new' : 'new';

      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          payment_type: `${membership.payment_method}`,
          items: [{
            item_name: `Membership - ${membership_form.name} - ${membership.selected_membership.value}`,
            item_id: `${membership_form.campaigncode}`,
            price: membership.amount,
            item_brand: `${membership.selected_membership.wakehurst_membership ? 'Wakehurst' : 'Kew'}`,
            item_category: `Membership - ${membership.selected_membership.value}`,
            item_category2: `${formType}`,
            quantity: 1
          }]
        }
      });
      setSS('dataLayerInitialPush3', true)
      setDataLayerInitialPush3(true)
    }

    if (membership.payment_type === 'Single') {
      if (membership.payment_method === 'CreditCard') {
        navigate(`/membership/${url}/card-details`)
      }
      if (membership.payment_method === 'PayPal') {
        braintreePayPalInit(membership)
      }
    }
    if (membership.payment_type === 'Regular') {
      navigate(`/membership/${url}/regular-billing`)
    }
    if (membership.payment_type === 'CardReader') {
      navigate(`/membership/${url}/card-reader`)
    }
  }

  return (
    <>
      { membership &&
        <>
          <Formik
            initialValues={ payment }
            validationSchema={ validation.payment }
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={ onSubmit }>
              {(props: any) => {
                props.errors = cleanObject(props.errors)
                return (
                  <>
                    <Form name="membershipForm">
                      <StepIndicator />
                      <PaymentDetails />
                      { !wakehurstMembership &&
                        <GiftAid {...props} />
                      }
                      <SubmitButtons {...props} />

                    </Form>
                  </>
                )
            }}
          </Formik>
        </>
      }
    </>
  )
}

export default Payment